import { ajv, MpxResponseMeta, TaskDetails } from '@cibo/core'
import { useBatchedFields } from '@cibo/landmanager'
import { BulkEditHeader } from '@cibo/landmanager/src/features/DetailEditing/EditMultipleFields/BulkEditHeader'
import { AutoFields, CiboAutoForm, ResponsiveDialog } from '@cibo/ui'
import AutoField from '@cibo/ui/src/components/Form/Uniforms/AutoField'
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { pick } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import JSONSchemaBridge from 'uniforms-bridge-json-schema'
import { SubmitField } from 'uniforms-mui'
import { useProgramEngagementContext } from '../../../../../context'
import { componentDetector } from './componentDetector'
import { DataEntrySchemaEditingState } from './types'

function createValidator(schema: object) {
  const validator = ajv.compile(schema)

  return (model: any) => {
    validator(model)
    return validator.errors?.length ? { details: validator.errors } : null
  }
}

export const SchemaTraitDialog = ({
  state,
  setState,
  saveTaskData,
}: {
  state: DataEntrySchemaEditingState
  setState(newState: { open: boolean }): void
  saveTaskData(updates: any): Promise<(MpxResponseMeta & TaskDetails) | void>
}) => {
  const { t } = useTranslation('@cibo/landmanager/DetailEditing')

  const [model, setModel] = useState<{ value: any }>()

  const { taskData } = useProgramEngagementContext()
  const detail =
    state.traitConfig &&
    state.detailRequirement &&
    taskData.data?.getDetail({
      traitConfig: state.traitConfig,
      resourceId: state.resourceIds?.[0],
      detailRequirement: state.detailRequirement,
    })

  useEffect(() => {
    if (detail && state.traitConfig?.detailValueKey) {
      setModel({ value: detail[state.traitConfig.detailValueKey] })
    }
  }, [state.traitConfig?.detailValueKey, detail])

  useEffect(() => {
    !state.open && setModel(undefined)
  }, [state.open])

  const bridge = useMemo<JSONSchemaBridge | undefined>(() => {
    if (!!state.traitConfig) {
      if (!state.traitConfig?.schema?.type) {
        console.error(
          "The configured schema does not have a 'type' property.\n\n",
          JSON.stringify(state.traitConfig.schema, null, 2),
          '\n\n'
        )
        throw new Error("Schema must include a 'type' property.")
      }

      const formSchema = {
        type: 'object',
        properties: { value: { $id: 'schema', ...state.traitConfig.schema } },
        required: ['value'],
      }

      return new JSONSchemaBridge({
        schema: formSchema,
        validator: createValidator(formSchema),
      })
    }

    return undefined
  }, [state.traitConfig?.schema])

  const fieldModels = useBatchedFields(state.resourceIds || [])

  const handleSubmit = useCallback(
    (formModel: any) => {
      if (!state.resourceIds || !state.detailRequirement) return

      const baseDetail = pick(['traitId', 'year', 'programId'], state.detailRequirement)

      const detailValueKey = state.traitConfig?.detailValueKey

      const updates = state.resourceIds.map(resourceId => ({
        resourceId,
        details: [
          {
            ...baseDetail,
            [detailValueKey as string]: formModel.value,
          },
        ],
      }))

      return saveTaskData({
        fieldDetails: updates,
      })
        .then(() => {
          state.onSuccess?.(formModel.value)
        })
        .catch(() => {})
    },
    [saveTaskData, state.resourceIds, state.detailRequirement]
  )

  if (!bridge) {
    return null
  }

  const uiParams = state.traitConfig?.schema?.['ui-params']

  if (!taskData.data || !state.detailRequirement) {
    return null
  }

  return (
    <ResponsiveDialog
      open={state.open}
      onClose={() => setState({ open: false })}
      data-testid="schema-trait-dialog"
    >
      <AutoField.componentDetectorContext.Provider value={componentDetector}>
        <CiboAutoForm
          schema={bridge}
          model={model}
          onSubmit={handleSubmit}
          onChangeModel={setModel}
          validate="onChange"
          warningSchema={
            uiParams?.warningSchema && {
              type: 'object',
              properties: { value: uiParams?.warningSchema },
            }
          }
        >
          <DialogTitle>
            {t('editSelectedFields', {
              traitId: state.traitConfig?.title,
              count: fieldModels.data?.length,
            })}
          </DialogTitle>

          <DialogContent>
            <BulkEditHeader fieldModels={fieldModels.data} />

            <AutoFields />
          </DialogContent>

          <DialogActions>
            <Button variant="outlined" onClick={() => setState({ open: false })}>
              {t('cancel')}
            </Button>

            <SubmitField label={t('save')} />
          </DialogActions>
        </CiboAutoForm>
      </AutoField.componentDetectorContext.Provider>
    </ResponsiveDialog>
  )
}
